import React, { useCallback, useEffect } from 'react';
import { PropTypes } from 'prop-types';
// styles
import styled, { css } from 'styled-components';
import { Flex, gridSizes, media, zIndexses } from 'utils/style';
// logic
import FeasibilityContextProvider from 'hooks/useFeasibility';
import { SuitableVehicleTypeContextProvider } from 'hooks/useSuitableVehicleType';
import { constants as orderConstants } from 'utils/order';
import { replace } from 'connected-react-router';
import routes from 'utils/routes';
import { compareObjects } from 'utils/object';

// redux
import { useDispatch, useSelector } from 'react-redux';
import { selectors as authSelectors } from 'state/auth';
import {
  selectors as orderSelectors,
  actions as orderActions,
} from 'state/order';
import { actions as ordersActions } from 'state/orders';
import {
  actions as analyticsActions,
  constants as analyticsEvents,
} from 'state/analytics';
import { actions as salesIntelligenceActions } from 'state/salesIntelligence';
// components
import AuthenticatedUserView from 'refactored/ui/views/authenticatedUserView';
import TypeOfOrderSelector from 'components/form/TypeOfOrderSelector';
import DirectForm from 'components/form/DirectForm';
import MRForm from 'components/form/MRForm';
import RecurringForm from 'components/form/RecurringForm';
import { QuotePrice } from 'components/Price';
import { userIsAuthenticatedRedir } from 'components/auth/hoc';
import PriceSuggest from 'components/Price/PriceSuggest';

const PriceSuggestTabletCSS = css`
  display: inherit;

  .price-suggest {
    &__suggestion {
      &:not(:first-child) {
        margin-left: 1rem;
      }
    }
  }
  div {
    flex-wrap: nowrap;
  }
`;

const PriceSuggestMobileCSS = css`
  display: inherit;
  div {
    flex-wrap: wrap;
  }
`;

const StyledPriceSuggest = styled(PriceSuggest)`
  display: none;
  ${media.lessThan('large')`${PriceSuggestTabletCSS}`}
  ${media.lessThan('small')`${PriceSuggestMobileCSS}`}
`;

const NewOrder = userIsAuthenticatedRedir(({ className }) => {
  const dispatch = useDispatch();

  const orderType = useSelector(orderSelectors.getTypeOfOrder);
  const user = useSelector(authSelectors.getUser, compareObjects);
  const isEditing = useSelector(orderSelectors.getIsEditing);
  const isRecurring = useSelector(orderSelectors.getIsRecurringOrder);
  const isDirect =
    [
      orderConstants.DIRECT_ORDER,
      orderConstants.DIRECT_ORDER_RECURRING,
    ].indexOf(orderType) >= 0;
  const isMultiStop =
    orderType === orderConstants.DISTRIBUTION ||
    orderType === orderConstants.COLLECTION;

  // reset data and go to default page
  const handleBack = useCallback(() => {
    dispatch(ordersActions.resetData());
    dispatch(replace(routes.orders.default));
  }, [dispatch]);

  //   effects

  // checkPaymentMethod
  useEffect(() => {
    dispatch(orderActions.checkPaymentMethod());
  }, [dispatch]);

  // send analytics form started event
  useEffect(() => {
    dispatch(
      analyticsActions.sendEvent(
        analyticsEvents.form.started({ type: orderType, user })
      )
    );
  }, [dispatch, orderType, user]);

  // set isRecurring
  useEffect(() => {
    if (orderType === orderConstants.DIRECT_ORDER_RECURRING) {
      dispatch(orderActions.setRecurring({ isRecurring: true }));
      dispatch(salesIntelligenceActions.setRecurringOrderSuggestion(null));
    }
  }, [orderType, dispatch]);

  return (
    <FeasibilityContextProvider>
      <SuitableVehicleTypeContextProvider>
        <AuthenticatedUserView className={className} handleBack={handleBack}>
          <section className="main" role="main" data-testid="new-order">
            <section className="form">
              {!isEditing && <TypeOfOrderSelector />}
              {isRecurring && <RecurringForm />}
              {isDirect && <DirectForm typeOfOrder={orderType} />}
              {isMultiStop && <MRForm typeOfOrder={orderType} />}
              {!isEditing && <StyledPriceSuggest />}
            </section>
            <aside className="quote">
              <QuotePrice
                typeOfOrder={orderType}
                fixed
                isRecurring={isRecurring}
              />
            </aside>
          </section>
        </AuthenticatedUserView>
      </SuitableVehicleTypeContextProvider>
    </FeasibilityContextProvider>
  );
});

NewOrder.propTypes = {
  className: PropTypes.string,
  orderType: PropTypes.string,
};

const mainStyles = css`
  ${Flex.row};
  margin: 0 auto;
  max-width: 1146px;
  min-height: 100%;
  align-items: stretch;
  flex-grow: 1;
  ${media.lessThan('large')`
    max-width: 100%;
    ${Flex.column};
  `};
`;

const formStyles = css`
  width: 100%;
  max-width: 68%;
  margin: 0 auto;
  overflow: scroll;

  padding: ${gridSizes('L')} ${gridSizes('L')};
  &::-webkit-scrollbar {
    width: 0px;
    background: transparent;
  }

  ${media.lessThan('large')`
    max-width: 100%;
  `};
`;

const quoteStyles = css`
  width: 100%;
  max-width: 32%;
  color: var(--grey700);
  font-weight: none;
  line-height: 1.33;
  padding: ${gridSizes('L')} ${gridSizes('L')} 0 ${gridSizes('L')};
  border-left: 1px solid var(--grey-mouse);
  ${media.lessThan('large')`
    background: #fff;
    position: sticky;
    bottom: 0;
    left: 0;
    max-width: 100%;
    border-top: 1px solid var(--grey-mouse);
    z-index: ${zIndexses.quotePrice};
    display: flex;
    align-items: center;
    padding: 0;
    border-left: none;
  `};

  ${media.lessThan('extraSmall')`
    box-shadow: 0 -1px 3px 0 rgba(0, 0, 0, 0.15);
  `};
`;

const StyledNewOrder = styled(NewOrder)`
  .main {
    ${mainStyles};
  }

  .form {
    ${formStyles};
  }

  .quote {
    ${quoteStyles};
  }
`;

export default StyledNewOrder;
